import React, { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Modal from "./Modal";
import { Button } from "./ui/button";
import { FieldFeedback, FormError } from "./ui/feedback";

/**
 * Reusable "Place item on HOLD" modal. Can be used inline from Inventory rows
 * or standalone on the Hold page.
 */
export default function HoldModal({ open, onClose, onCreated, item, warehouse }) {
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setQty(1);
      setNotes("");
      setError("");
    }
  }, [open]);

  if (!open || !item || !warehouse) return null;
  const available = item.stock?.[warehouse.id] ?? 0;
  const over = Number(qty) > available;

  const submit = async () => {
    if (loading) return;
    const quantity = Number(qty);
    if (!Number.isInteger(quantity) || quantity < 1) return setError("Enter a whole quantity");
    if (over) return setError("Quantity exceeds available stock");
    setLoading(true);
    setError("");
    try {
      await api.post("/holds", {
        item_id: item.id,
        warehouse_id: warehouse.id,
        quantity,
        notes,
      });
      toast.success(`Hold placed on ${item.part_number}`);
      onCreated?.();
      onClose();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Place item on Hold" onClose={onClose} busy={loading} testid="inline-hold-modal">
      <div className="space-y-4">
        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm">
          <div className="flex justify-between items-baseline gap-3">
            <div>
              <div className="font-medium">{item.part_name}</div>
              <div className="font-mono text-xs text-zinc-700">{item.part_number}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-wider uppercase text-zinc-500">
                {warehouse.name}
              </div>
              <div className="text-xs font-mono">
                Available: <span className="font-medium">{available}</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">
            Quantity to hold
          </div>
          <input
            type="number"
            min="1"
            max={available}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className={`wms-input ${over ? "border-destructive" : ""}`}
            data-testid="inline-hold-qty-input"
          />
          <FieldFeedback>{over ? `Exceeds available (${available})` : ""}</FieldFeedback>
        </div>

        <FormError>{error}</FormError>

        <div>
          <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">
            Notes
          </div>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="wms-input"
            placeholder="Reason for hold (optional)"
            data-testid="inline-hold-notes-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
          <Button type="button" variant="cancel"
            onClick={onClose}
            data-testid="inline-hold-cancel"
          >
            Cancel
          </Button>
          <Button type="button" variant="success"
            onClick={submit}
            disabled={over}
            loading={loading}
            loadingLabel="Placing hold…"
            data-testid="inline-hold-submit"
          >
            Place hold
          </Button>
        </div>
      </div>
    </Modal>
  );
}
